import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TemplateConfig, TemplateDetailView, TemplateSummaryView, TemplateVersionView } from '@lp/contracts';
import { ArrowLeft, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorText, get, patch, post } from '@/lib/api';
import { useIsSuperAdmin } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { SettingsList } from '@/components/settings';
import { NamesDialog } from '@/components/edit-dialogs';
import { Badge, Button, Card, ErrorBox, Loading, PageHeader } from '@/components/ui';
import { StateBadge } from './clients';
import { TemplateStatusBadge } from './templates';

/** what one template version gives: its products (with a company's own names) and its settings */
export function TemplateContent({ config }: { config: TemplateConfig }) {
  const t = useT();
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Products')}</h3>
        {!config.products.length ? (
          <p className="text-sm text-ink-500">{t('None')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {config.products.map((p) => (
              <li key={p.code}>
                <span className="font-mono text-xs">{p.code}</span>
                {(p.nameEn || p.nameKm) && (
                  <span className="ml-2 text-ink-600">
                    {t('Own name')}: {[p.nameEn, p.nameKm].filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Settings')}</h3>
        <SettingsList map={config.settings} />
      </div>
      <div className="md:col-span-2">
        <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{t('Levels')}</h3>
        {!config.levels.length ? (
          <p className="text-sm text-ink-500">{t('None')}</p>
        ) : (
          <ul className="space-y-0.5 text-sm" data-testid="template-levels">
            {config.levels.map((l) => (
              <li key={l.code}>
                <span className="font-medium">{l.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{l.code}</span> <span className="text-xs text-ink-500">— {t('Under')}: {l.allowedUnder.map((u) => u ?? t('the company')).join(', ')}</span>
                {l.canSell && <span className="ml-1.5 text-xs text-emerald-700">{t('Sells')}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function TemplatePage() {
  const t = useT();
  const code = (useParams().code ?? '').toUpperCase();
  const nav = useNavigate();
  const qc = useQueryClient();
  const superAdmin = useIsSuperAdmin();
  const [viewing, setViewing] = useState<number | null>(null);
  const [naming, setNaming] = useState(false);
  const detail = useQuery({ queryKey: ['template', code], queryFn: () => get<TemplateDetailView>(`/platform/templates/${code}`) });
  const shown = viewing ?? detail.data?.publishedVersion ?? detail.data?.draftVersion ?? null;
  const version = useQuery({ queryKey: ['template', code, 'version', shown], queryFn: () => get<TemplateVersionView>(`/platform/templates/${code}/versions/${shown}`), enabled: shown != null });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['template', code] });
    void qc.invalidateQueries({ queryKey: ['templates'] });
  };
  const newDraft = useMutation({ mutationFn: () => post<TemplateVersionView>(`/platform/templates/${code}/versions`), onSuccess: (v) => nav(`/templates/${code}/versions/${v.version}/edit`) });
  const status = useMutation({ mutationFn: (s: 'active' | 'archived') => patch<TemplateSummaryView>(`/platform/templates/${code}`, { status: s }), onSuccess: refresh });
  const move = useMutation({ mutationFn: (v: number) => post<{ moved: number[]; refused: { clientId: number; code: string; reason: string }[] }>(`/platform/templates/${code}/move-clients`, { version: v }), onSuccess: refresh });

  if (detail.isLoading) return <Loading />;
  if (detail.error || !detail.data) return <ErrorBox text={errorText(detail.error)} />;
  const p = detail.data;
  const behind = p.publishedVersion ? p.clientList.filter((c) => c.version !== p.publishedVersion) : [];

  return (
    <>
      <Link to="/templates" className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {t('All templates')}
      </Link>
      <PageHeader
        title={p.nameEn}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{p.code}</span>
            <span>{p.nameKm}</span>
            <TemplateStatusBadge status={p.status} />
            {p.publishedVersion && <Badge>v{p.publishedVersion}</Badge>}
          </span>
        }
        actions={
          superAdmin && (
            <>
              <Button variant="secondary" icon={<Pencil size={14} />} onClick={() => setNaming(true)}>
                {t('Edit')}
              </Button>
              <Button variant="secondary" onClick={() => status.mutate(p.status === 'active' ? 'archived' : 'active')} disabled={status.isPending}>
                {t(p.status === 'active' ? 'Archive' : 'Make active')}
              </Button>
              {p.draftVersion ? (
                <Button icon={<Pencil size={14} />} onClick={() => nav(`/templates/${code}/versions/${p.draftVersion}/edit`)}>
                  {t('Edit draft')} v{p.draftVersion}
                </Button>
              ) : (
                <Button icon={<Plus size={14} />} onClick={() => newDraft.mutate()} disabled={newDraft.isPending}>
                  {t('New version')}
                </Button>
              )}
            </>
          )
        }
      />
      {p.description && <p className="-mt-3 mb-4 text-sm text-ink-600">{p.description}</p>}
      {naming && <NamesDialog title={`${t('Edit')} — ${p.code}`} value={p} withDescription onSave={async (v) => { await patch(`/platform/templates/${code}`, v); refresh(); }} onClose={() => setNaming(false)} />}
      {(newDraft.error || status.error) && <ErrorBox text={errorText(newDraft.error ?? status.error)} />}

      <Card
        title={shown ? `${t('Version')} ${shown}${version.data?.status === 'draft' ? ` · ${t('Draft')}` : ''}` : t('What it gives')}
        actions={
          viewing && (
            <Button variant="ghost" onClick={() => setViewing(null)}>
              {t('Back to the published version')}
            </Button>
          )
        }
      >
        {!shown ? <p className="text-sm text-ink-600">{t('No published version yet.')}</p> : version.isLoading ? <Loading /> : version.data ? <TemplateContent config={version.data.config} /> : <ErrorBox text={errorText(version.error)} />}
        {version.data?.note && <p className="mt-3 border-t border-ink-100 pt-2 text-xs text-ink-500">{version.data.note}</p>}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card
          title={`${t('Clients on this template')} (${p.clientList.length})`}
          actions={
            superAdmin &&
            behind.length > 0 && (
              <Button variant="secondary" onClick={() => move.mutate(p.publishedVersion!)} disabled={move.isPending}>
                {t('Move all to')} v{p.publishedVersion} ({behind.length})
              </Button>
            )
          }
        >
          {!p.clientList.length ? (
            <p className="text-sm text-ink-600">{t('No client is on this template.')}</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {p.clientList.map((c) => (
                  <tr key={c.id} className="border-b border-ink-100 last:border-0">
                    <td className="py-2 pr-3">
                      <Link to={`/clients/${c.id}`} className="font-medium text-ink-900 hover:underline">
                        {c.nameEn}
                      </Link>{' '}
                      <span className="font-mono text-[11px] text-ink-500">{c.code}</span>
                    </td>
                    <td className="py-2 pr-3">
                      <StateBadge state={c.state as 'testing'} />
                    </td>
                    <td className="py-2 text-right">
                      <Badge tone={c.version === p.publishedVersion ? 'gray' : 'amber'}>v{c.version}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {move.data && (
            <div className="mt-3 space-y-1 border-t border-ink-100 pt-2 text-xs">
              <p className="text-emerald-700">
                {t('Moved')}: {move.data.moved.length}
              </p>
              {move.data.refused.map((r) => (
                <p key={r.clientId} className="text-red-700">
                  {r.code}: {r.reason}
                </p>
              ))}
            </div>
          )}
          {move.error && <ErrorBox text={errorText(move.error)} />}
        </Card>

        <Card title={t('Versions')}>
          <table className="w-full text-sm">
            <tbody>
              {p.versions.map((v) => (
                <tr key={v.version} className="border-b border-ink-100 last:border-0">
                  <td className="py-2 pr-3">
                    v{v.version} {v.status === 'draft' && <Badge tone="blue">{t('Draft')}</Badge>}
                    {v.note && <span className="block text-xs text-ink-500">{v.note}</span>}
                  </td>
                  <td className="py-2 pr-3 text-xs text-ink-500">{dateTime(v.publishedAt)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{v.clients}</td>
                  <td className="py-2 text-right">
                    {v.status === 'draft' && superAdmin ? (
                      <Button variant="ghost" onClick={() => nav(`/templates/${code}/versions/${v.version}/edit`)}>
                        {t('Edit')}
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => setViewing(v.version)}>
                        {t('View')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
